import { inject, Injectable, NgZone } from '@angular/core'

import { Observable, Subject } from 'rxjs'

import { ENVIRONMENT } from '../tokens/environment.token'

interface ChannelConfig {
  path: string
  eventKey: string
}

interface WebSocketMessage {
  [key: string]: unknown
  data?: unknown
}

interface ChannelState {
  ws?: WebSocket
  subjects: Map<string, Subject<unknown>>
  queue: WebSocketMessage[]
  reconnectAttempts: number
  connecting: boolean
}

@Injectable({ providedIn: 'root' })
export class SocketService {
  private readonly env = inject(ENVIRONMENT)
  private readonly ngZone = inject(NgZone)

  private readonly channelConfigs: Record<string, ChannelConfig> = {
    chat: { path: '/ws/chat/', eventKey: 'type' },
    protocol: { path: '/ws/protocol/', eventKey: 'event' }
  }

  private readonly maxReconnectAttempts = 5
  private readonly reconnectDelay = 1000

  // Use path as key to avoid duplicate connections for the same endpoint
  private readonly channelStates = new Map<string, ChannelState>()

  listen<T>(event: string, channel = 'chat'): Observable<T> {
    const { path } = this.getChannelConfig(channel)
    const state = this.ensureChannel(path)

    let subject = state.subjects.get(event) as Subject<T> | undefined
    if (!subject) {
      subject = new Subject<T>()
      state.subjects.set(event, subject as Subject<unknown>)
    }

    return subject.asObservable()
  }

  emit(event: string, data?: unknown, channel = 'chat') {
    const config = this.getChannelConfig(channel)
    const state = this.ensureChannel(config.path)
    const message: WebSocketMessage = { [config.eventKey]: event, data }

    if (!state.ws || state.ws.readyState !== WebSocket.OPEN) {
      state.queue.push(message)
      return
    }

    this.safeSend(state, message)
  }

  disconnect(channel?: string) {
    if (channel) {
      const { path } = this.getChannelConfig(channel)
      const state = this.channelStates.get(path)
      state?.ws?.close()
      this.channelStates.delete(path)
      return
    }

    for (const [path, state] of this.channelStates.entries()) {
      state.ws?.close()
      this.channelStates.delete(path)
    }
  }

  private ensureChannel(path: string): ChannelState {
    let state = this.channelStates.get(path)
    if (!state) {
      state = {
        ws: undefined,
        subjects: new Map<string, Subject<unknown>>(),
        queue: [],
        reconnectAttempts: 0,
        connecting: false
      }
      this.channelStates.set(path, state)
      this.connectChannel(path, state)
    } else if (!state.ws || state.ws.readyState === WebSocket.CLOSED) {
      this.connectChannel(path, state)
    }

    return state
  }

  private connectChannel(path: string, state: ChannelState) {
    if (state.connecting) return
    state.connecting = true

    const url = `${this.env.socketUrl}${path}`
    const config = this.getConfigByPath(path)
    const eventKey = config.eventKey

    state.ws = new WebSocket(url)

    state.ws.onopen = () => {
      state.reconnectAttempts = 0
      state.connecting = false
      const pending = [...state.queue]
      state.queue = []
      for (const msg of pending) {
        this.safeSend(state!, msg)
      }
    }

    state.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data)
        const eventName = message[eventKey] as string | undefined
        if (!eventName) return
        const subject = state!.subjects.get(eventName)
        if (subject) {
          this.ngZone.run(() => subject.next(message.data))
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    state.ws.onclose = () => {
      state.connecting = false
      state.ws = undefined
      if (state!.reconnectAttempts < this.maxReconnectAttempts) {
        state!.reconnectAttempts++
        setTimeout(
          () => this.connectChannel(path, state!),
          this.reconnectDelay * state!.reconnectAttempts
        )
      }
    }

    state.ws.onerror = (error) => {
      state.connecting = false
      console.error(`WebSocket error on ${path}:`, error)
    }
  }

  private safeSend(state: ChannelState, message: WebSocketMessage) {
    try {
      state.ws?.send(JSON.stringify(message))
    } catch (error) {
      console.error('Failed to send WebSocket message:', error)
    }
  }

  private getChannelConfig(channel: string): ChannelConfig {
    return (
      this.channelConfigs[channel] ?? {
        path: `/ws/${channel}/`,
        eventKey: 'type'
      }
    )
  }

  private getConfigByPath(path: string): ChannelConfig {
    const found = Object.values(this.channelConfigs).find((c) => c.path === path)
    if (found) return found
    return { path, eventKey: 'type' }
  }
}
